import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { seoArticles, shoppingGuideArticle } from '@/mocks/seoContent';

export default function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const closeAll = () => {
    setIsMobileOpen(false);
    setOpenSection(null);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" onClick={closeAll}>
            <span className="text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
              YOYBUY<span className="font-light text-foreground-600">.REST</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              {t('nav.home')}
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => toggleSection('categories')}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('nav.categories')}
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${openSection === 'categories' ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {openSection === 'categories' && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl py-2 shadow-lg border border-background-200/70 z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      {cat.nameRu}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => toggleSection('guide')}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                Гид по покупкам
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${openSection === 'guide' ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {openSection === 'guide' && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl py-2 shadow-lg border border-background-200/70 z-50">
                  <Link
                    to="/article/gid-po-pokupkam"
                    onClick={() => setOpenSection(null)}
                    className="block px-4 py-2.5 text-sm font-medium text-foreground-900 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    {shoppingGuideArticle.title}
                  </Link>
                  <div className="border-t border-background-200/70 my-1"></div>
                  <p className="px-4 py-1.5 text-xs text-foreground-500 font-light">SEO Статьи</p>
                  {seoArticles.slice(0, 5).map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setOpenSection(null)}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors truncate"
                    >
                      {article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.buy_now')}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>

            <button
              onClick={() => {
                setIsMobileOpen(!isMobileOpen);
                setOpenSection(null);
              }}
              className="md:hidden relative z-[70] w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`text-lg ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </span>
            </button>
          </div>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-background-50 flex flex-col pt-20 pb-6 px-4 overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={closeAll}
              className="px-4 py-3 text-sm font-medium text-foreground-950 hover:bg-background-100 rounded-xl transition-colors flex items-center gap-2"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              {t('nav.home')}
            </Link>

            <button
              onClick={() => toggleSection('mobile-categories')}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-xl transition-colors flex items-center justify-between w-full cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-grid-line text-sm"></i>
                </span>
                {t('nav.categories')}
              </span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${openSection === 'mobile-categories' ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </span>
            </button>

            {openSection === 'mobile-categories' && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/70 space-y-0.5 mb-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeAll}
                    className="block px-4 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    {cat.nameRu}
                  </a>
                ))}
              </div>
            )}

            <button
              onClick={() => toggleSection('mobile-guide')}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-xl transition-colors flex items-center justify-between w-full cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line text-sm"></i>
                </span>
                Гид по покупкам
              </span>
              <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${openSection === 'mobile-guide' ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </span>
            </button>

            {openSection === 'mobile-guide' && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/70 space-y-0.5 mb-1">
                <Link
                  to="/article/gid-po-pokupkam"
                  onClick={closeAll}
                  className="block px-4 py-2.5 text-sm font-medium text-foreground-800 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
                >
                  {shoppingGuideArticle.title}
                </Link>
                {seoArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    onClick={closeAll}
                    className="block px-4 py-2 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors truncate"
                  >
                    {article.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="mt-auto pt-6">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-5 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors cursor-pointer"
            >
              {t('nav.buy_now')}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}